/**
 * Helpers to deal with lead attachment storage references.
 *
 * Lead attachments are stored in the private `lead-attachments` Supabase
 * Storage bucket. Historically, the LeadForm stored full *public* URLs in
 * `leads.file_urls` (e.g. `https://<project>.supabase.co/storage/v1/object/public/lead-attachments/temp/<file>`),
 * but the bucket is private so those URLs return 403. Going forward we
 * store storage *paths* (e.g. `temp/<uuid>.jpg`) and mint signed URLs
 * on-demand for admin viewing and outbound webhook delivery.
 *
 * These helpers also gracefully handle the legacy data shape so existing
 * leads remain accessible.
 */

const BUCKET = "lead-attachments";

/**
 * Extract the storage path inside the `lead-attachments` bucket from any
 * supported entry shape. Returns `null` if nothing usable is found.
 *
 * Supported inputs:
 *   - A clean storage path:           `"temp/abc.jpg"` → `"temp/abc.jpg"`
 *   - A legacy public URL:            `".../object/public/lead-attachments/temp/abc.jpg"` → `"temp/abc.jpg"`
 *   - A legacy signed URL:            `".../object/sign/lead-attachments/temp/abc.jpg?token=..."` → `"temp/abc.jpg"`
 *   - A future structured object:     `{ path: "temp/abc.jpg", ... }` → `"temp/abc.jpg"`
 */
export function extractAttachmentPath(entry: unknown): string | null {
  if (!entry) return null;

  // Object shape (forward-compatible with richer metadata)
  if (typeof entry === "object" && entry !== null && !Array.isArray(entry)) {
    const maybePath = (entry as { path?: unknown }).path;
    if (typeof maybePath === "string" && maybePath.length > 0) {
      return stripBucketPrefix(maybePath);
    }
    return null;
  }

  if (typeof entry !== "string") return null;
  const value = entry.trim();
  if (!value) return null;

  // Full URL -> extract whatever follows the bucket name
  // Matches both `/object/public/<bucket>/...` and `/object/sign/<bucket>/...`
  const bucketMatch = value.match(
    new RegExp(`/object/(?:public|sign|authenticated)/${BUCKET}/([^?#]+)`)
  );
  if (bucketMatch && bucketMatch[1]) {
    return decodeURIComponent(bucketMatch[1]);
  }

  // If it's a URL but doesn't contain our bucket path, give up
  if (/^https?:\/\//i.test(value)) {
    return null;
  }

  return stripBucketPrefix(value);
}

function stripBucketPrefix(path: string): string {
  return path.replace(new RegExp(`^/?${BUCKET}/`), "").replace(/^\/+/, "");
}

/**
 * Derive a friendly file name from a storage path.
 * `"temp/2025/abc.jpg"` -> `"abc.jpg"`
 */
export function attachmentDisplayName(path: string): string {
  const parts = path.split("/");
  return parts[parts.length - 1] || path;
}

/**
 * Normalize the raw `file_urls` JSON column into an array of storage paths,
 * filtering out anything we can't resolve.
 */
export function normalizeFileUrls(raw: unknown): string[] {
  if (!raw) return [];

  // Some legacy rows store the JSON array as a JSON-encoded string
  let value: unknown = raw;
  if (typeof value === "string") {
    try {
      value = JSON.parse(value);
    } catch {
      // Single string entry — treat as one item
      value = [value];
    }
  }

  if (!Array.isArray(value)) return [];

  return value
    .map(extractAttachmentPath)
    .filter((p): p is string => typeof p === "string" && p.length > 0);
}
